/**
 * MenuItemModal - Menu item create/edit modal component
 * Includes ingredient templates and modifier management
 */
import { useState, useEffect } from 'react';
import { Plus, X, Upload, Sparkles, ChevronDown, ChevronUp, Trash2, Info, AlertCircle, Lock, Unlock, Wand2, Loader2, Video, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { api } from '@/utils/apiClient';

export default function MenuItemModal({ isOpen, onClose, item, defaultCategory = 'coffee', categories = [], onSave }) {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', category: defaultCategory,
    image: '', video: '', is_featured: false, is_bestseller: false, is_available: true,
    is_coffee_pass_eligible: false, // Coffee Pass eligibility - only for coffee category
    ingredients: [], modifiers: [], size_modifiers: [], sizes: [],
    allow_category_addons: true
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newIngredient, setNewIngredient] = useState({ name: '', removable: true, extra_price: 0 });
  const [newModifier, setNewModifier] = useState({ name: '', price: 0, description: '' });
  const [newSize, setNewSize] = useState({ name: '', price: '' });
  const [suggestedTemplates, setSuggestedTemplates] = useState([]);
  const [showTemplateSection, setShowTemplateSection] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price.toString(),
        category: item.category,
        image: item.image,
        video: item.video || '',
        is_featured: item.is_featured,
        is_bestseller: item.is_bestseller,
        is_available: item.is_available,
        is_coffee_pass_eligible: item.is_coffee_pass_eligible || false,
        ingredients: item.ingredients || item.customization?.ingredients || [],
        modifiers: item.modifiers || item.customization?.modifiers || [],
        size_modifiers: item.customization?.size_modifiers || [],
        sizes: item.sizes || [],
        allow_category_addons: item.allow_category_addons !== false // Default to true if not set
      });
    } else {
      setFormData({
        name: '', description: '', price: '', category: defaultCategory,
        image: '', video: '', is_featured: false, is_bestseller: false, is_available: true,
        is_coffee_pass_eligible: false,
        ingredients: [], modifiers: [], size_modifiers: [], sizes: [],
        allow_category_addons: true
      });
    }
    setNewIngredient({ name: '', removable: true, extra_price: 0 });
    setNewModifier({ name: '', price: 0, description: '' });
    setNewSize({ name: '', price: '' });
    setActiveTab('basic');
    setShowTemplateSection(false);
  }, [item, isOpen, defaultCategory]);

  // Fetch suggested templates when category changes
  useEffect(() => {
    if (formData.category && isOpen) {
      fetchSuggestedTemplates(formData.category);
    }
  }, [formData.category, isOpen]);

  const fetchSuggestedTemplates = async (category) => {
    try {
      const res = await api.get(`/admin/menu/suggest-template/${category}`);
      setSuggestedTemplates(res.data.suggested_templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const applyTemplate = (template) => {
    setApplyingTemplate(true);
    
    // Apply template ingredients
    const newIngredients = template.ingredients.map(ing => ({
      name: ing.name,
      removable: ing.removable !== false,
      extra_price: ing.extra_price || 0,
      default_included: ing.default_included !== false
    }));
    
    // Apply template modifiers
    const newModifiers = template.modifiers.map(mod => ({
      name: mod.name,
      price: mod.price || 0,
      description: mod.description || ''
    }));
    
    // Apply size modifiers
    const newSizeModifiers = template.size_modifiers || [];
    
    setFormData(prev => ({
      ...prev,
      ingredients: newIngredients,
      modifiers: newModifiers,
      size_modifiers: newSizeModifiers
    }));
    
    toast.success(`Applied "${template.name}" template`);
    setApplyingTemplate(false);
    setShowTemplateSection(false);
  };

  const parseIngredientsFromDescription = async () => {
    if (!formData.description) {
      toast.error('Add a description first');
      return;
    }
    
    try {
      const res = await api.post('/menu/parse-ingredients', { description: formData.description });
      const data = res.data;
      const parsedIngredients = data.ingredients.map(name => ({
        name,
        removable: true,
        extra_price: 0,
        default_included: true
      }));
        
        // Merge with existing (don't add duplicates)
        const existingNames = new Set(formData.ingredients.map(i => i.name.toLowerCase()));
        const newIngredients = parsedIngredients.filter(
          i => !existingNames.has(i.name.toLowerCase())
        );
        
        if (newIngredients.length > 0) {
          setFormData(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, ...newIngredients]
          }));
          toast.success(`Found ${newIngredients.length} ingredients in description`);
        } else {
          toast.info('No new ingredients found in description');
        }
    } catch (err) {
      toast.error('Failed to parse ingredients');
    }
  };

  // Ingredient management
  const addIngredient = () => {
    if (!newIngredient.name.trim()) return;
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { 
        ...newIngredient, 
        extra_price: parseFloat(newIngredient.extra_price) || 0,
        default_included: true
      }]
    }));
    setNewIngredient({ name: '', removable: true, extra_price: 0 });
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const updateIngredient = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  // Modifier management
  const addModifier = () => {
    if (!newModifier.name.trim()) return;
    setFormData(prev => ({
      ...prev,
      modifiers: [...prev.modifiers, { 
        ...newModifier, 
        price: parseFloat(newModifier.price) || 0 
      }]
    }));
    setNewModifier({ name: '', price: 0, description: '' });
  };

  const removeModifier = (index) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.filter((_, i) => i !== index)
    }));
  };

  // Size management
  const addSize = () => {
    if (!newSize.name.trim() || !newSize.price) return;
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, { 
        name: newSize.name.trim(), 
        price: parseFloat(newSize.price) 
      }]
    }));
    setNewSize({ name: '', price: '' });
  };

  const removeSize = (index) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.filter((_, i) => i !== index)
    }));
  };

  // Size modifier management (for No Ice pricing)
  const updateSizeModifierPrice = (sizeName, modifierName, price) => {
    setFormData(prev => {
      const existing = prev.size_modifiers.findIndex(
        sm => sm.size === sizeName && sm.modifier_name === modifierName
      );
      
      let newSizeModifiers;
      if (existing >= 0) {
        if (price === null || price === '') {
          // Remove if price is empty
          newSizeModifiers = prev.size_modifiers.filter((_, i) => i !== existing);
        } else {
          // Update existing
          newSizeModifiers = prev.size_modifiers.map((sm, i) => 
            i === existing ? { ...sm, price: parseFloat(price) } : sm
          );
        }
      } else if (price !== null && price !== '') {
        // Add new
        newSizeModifiers = [...prev.size_modifiers, {
          size: sizeName,
          modifier_name: modifierName,
          price: parseFloat(price)
        }];
      } else {
        newSizeModifiers = prev.size_modifiers;
      }
      
      return { ...prev, size_modifiers: newSizeModifiers };
    });
  };

  const getSizeModifierPrice = (sizeName, modifierName) => {
    const sm = formData.size_modifiers.find(
      sm => sm.size === sizeName && sm.modifier_name === modifierName
    );
    return sm?.price ?? '';
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await api.post('/admin/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const data = res.data;
      const baseUrl = window.location.hostname.includes('bulaccinocafe.com') ? '' : (process.env.REACT_APP_BACKEND_URL || '');
      setFormData(prev => ({ ...prev, image: `${baseUrl}${data.url}` }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Failed to upload image');
    }
    setUploading(false);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('video/mp4') && !file.type.includes('video/quicktime')) {
      toast.error('Only MP4 video files are allowed');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Video too large. Max size is 50MB, got ${(file.size / 1024 / 1024).toFixed(1)}MB`);
      return;
    }

    setUploadingVideo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await api.post('/admin/upload-video', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const data = res.data;
      const baseUrl = window.location.hostname.includes('bulaccinocafe.com') ? '' : (process.env.REACT_APP_BACKEND_URL || '');
      setFormData(prev => ({ ...prev, video: `${baseUrl}${data.url}` }));
      toast.success(`Video uploaded (${data.size_mb}MB)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload video');
    }
    setUploadingVideo(false);
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: '' }));
    toast.success('Video removed');
  };

  // Generate AI image using item name, description, and category
  const handleGenerateAIImage = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an item name first');
      return;
    }

    setGeneratingImage(true);
    try {
      const res = await api.post('/admin/generate-ai-image', {
        name: formData.name,
        description: formData.description || '',
        category: formData.category || 'other'
      });

      const data = res.data;

      if (data.success && data.url) {
        // The URL returned is relative, prepend the base URL
        const baseUrl = window.location.hostname.includes('bulaccinocafe.com') ? '' : (process.env.REACT_APP_BACKEND_URL || '');
        const fullUrl = data.url.startsWith('http') ? data.url : `${baseUrl}${data.url}`;
        setFormData(prev => ({ ...prev, image: fullUrl }));
        toast.success('AI image generated!');
      } else {
        toast.error(data.error || 'Failed to generate image');
      }
    } catch (err) {
      console.error('AI image generation error:', err);
      toast.error('Failed to generate AI image');
    }
    setGeneratingImage(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build customization object
      const customization = {
        ingredients: formData.ingredients,
        modifiers: formData.modifiers,
        size_modifiers: formData.size_modifiers,
        allow_special_instructions: true
      };
      
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        customization
      };
      
      if (item) {
        await api.put(`/admin/menu/${item.id}`, payload);
      } else {
        await api.post('/admin/menu', payload);
      }

      toast.success(item ? 'Item updated' : 'Item created');
      onSave();
      onClose();
    } catch (err) {
      toast.error('Failed to save item');
    }
    setLoading(false);
  };

  // Check if item has "No Ice" modifier
  const hasNoIceModifier = formData.modifiers.some(m => 
    m.name.toLowerCase().includes('no ice')
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {item ? 'Edit Menu Item' : 'Add Menu Item'}
          </DialogTitle>
          <DialogDescription>
            {item ? 'Update the menu item details below.' : 'Fill in the details to add a new menu item.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="sizes">Sizes</TabsTrigger>
            <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
            <TabsTrigger value="modifiers">Modifiers</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic">
            <form className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="menu-item-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Base Price (FJD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    required
                    data-testid="menu-item-price"
                  />
                  {formData.sizes?.length > 0 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Sizes are optional upsells. The base price above will be the default.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  data-testid="menu-item-description"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger data-testid="menu-item-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Image</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="Image URL or upload"
                      className="flex-1"
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <Button type="button" variant="outline" disabled={uploading} asChild>
                        <span>
                          <Upload className="w-4 h-4" />
                        </span>
                      </Button>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGenerateAIImage}
                      disabled={generatingImage || !formData.name.trim()}
                      className="gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-300 dark:border-purple-700 hover:from-purple-500/20 hover:to-pink-500/20"
                      title="Generate AI image based on item name"
                      data-testid="generate-ai-image-btn"
                    >
                      {generatingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4 text-purple-600" />
                      )}
                      <span className="hidden sm:inline text-purple-700 dark:text-purple-300">
                        {generatingImage ? 'Generating...' : 'AI'}
                      </span>
                    </Button>
                  </div>
                  {!formData.image && formData.name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Wand2 className="w-3 h-3" />
                      Click AI to auto-generate an image based on the item name
                    </p>
                  )}
                </div>
              </div>

              {formData.image && (
                <div className="aspect-video max-w-xs rounded-xl overflow-hidden">
                  <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}

              {/* Video Upload Section */}
              <div className="space-y-2 p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 rounded-xl border border-violet-200 dark:border-violet-800">
                <div className="flex items-center gap-2 mb-2">
                  <Film className="w-5 h-5 text-violet-600" />
                  <Label className="text-sm font-semibold text-violet-800 dark:text-violet-200">Video (Optional)</Label>
                </div>
                <p className="text-xs text-violet-600 dark:text-violet-400 mb-3">
                  Upload an MP4 video (max 50MB) to play instead of the static image when customers view this item.
                </p>
                
                <div className="flex gap-2">
                  <Input
                    value={formData.video}
                    onChange={(e) => setFormData(prev => ({ ...prev, video: e.target.value }))}
                    placeholder="Video URL or upload an MP4 file"
                    className="flex-1"
                    data-testid="menu-item-video-url"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime"
                      className="hidden"
                      onChange={handleVideoUpload}
                      data-testid="menu-item-video-upload"
                    />
                    <Button type="button" variant="outline" disabled={uploadingVideo} asChild className="border-violet-300 hover:bg-violet-100 dark:border-violet-700 dark:hover:bg-violet-900">
                      <span>
                        {uploadingVideo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Video className="w-4 h-4 text-violet-600" />
                        )}
                      </span>
                    </Button>
                  </label>
                  {formData.video && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={removeVideo}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid="remove-video-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {formData.video && (
                  <div className="mt-3 aspect-video max-w-xs rounded-xl overflow-hidden bg-black">
                    <video 
                      src={formData.video} 
                      className="w-full h-full object-contain"
                      controls
                      muted
                      loop
                      playsInline
                      data-testid="video-preview"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4 md:gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_bestseller}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_bestseller: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Bestseller</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_available}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Available</span>
                </label>
              </div>
              
              {/* Category Add-ons Toggle */}
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allow_category_addons}
                    onChange={(e) => setFormData(prev => ({ ...prev, allow_category_addons: e.target.checked }))}
                    className="rounded w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium">Allow Category Add-ons</span>
                    <p className="text-xs text-muted-foreground">
                      When enabled, customers can add category-level add-ons (e.g., avocado, bacon) when customizing this item. 
                      Disable for items like Fries that shouldn't have certain add-ons.
                    </p>
                  </div>
                </label>
              </div>
              
              {/* Bula Coffee Pass Eligibility - Only shown for coffee category */}
              {formData.category === 'coffee' && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_coffee_pass_eligible}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_coffee_pass_eligible: e.target.checked }))}
                      className="rounded w-5 h-5 accent-green-600"
                      data-testid="coffee-pass-eligible-toggle"
                    />
                    <div>
                      <span className="text-sm font-medium text-green-800 dark:text-green-200">
                        ☕ Part of Bula Coffee Pass Program
                      </span>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        When enabled, Coffee Pass members can redeem this item as their free daily coffee.
                        Size upgrades (+$3) and milk upgrades still apply.
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </form>
          </TabsContent>

          {/* Sizes Tab */}
          <TabsContent value="sizes">
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
                  Sizes are optional upsells - not required!
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  The base price (set above) is the default. Customers can optionally choose a size for a different price.
                  Example: Coffee $6, Large option at $9.
                </p>
              </div>
              
              {/* Existing Sizes */}
              {formData.sizes.length > 0 && (
                <div className="space-y-2">
                  {formData.sizes.map((size, index) => (
                    <div key={`size-${size.name}-${index}`} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                      <span className="flex-1 font-medium">{size.name}</span>
                      <span className="text-primary font-medium">${size.price.toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={() => removeSize(index)}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 dark:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Size */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Size Name</Label>
                  <Input
                    value={newSize.name}
                    onChange={(e) => setNewSize(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Small, Large, Regular"
                  />
                </div>
                <div className="w-28">
                  <Label className="text-xs">Price (FJD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newSize.price}
                    onChange={(e) => setNewSize(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <Button type="button" onClick={addSize}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Ingredients Tab */}
          <TabsContent value="ingredients">
            <div className="space-y-4 mt-4">
              {/* Template Section */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                <button
                  type="button"
                  onClick={() => setShowTemplateSection(!showTemplateSection)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="font-medium">Quick Start with Templates</span>
                  </div>
                  {showTemplateSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {showTemplateSection && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Apply a predefined template to quickly add ingredients and modifiers.
                    </p>
                    {suggestedTemplates.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {suggestedTemplates.map(template => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => applyTemplate(template)}
                            disabled={applyingTemplate}
                            className="p-3 rounded-xl border-2 border-border hover:border-primary bg-card text-left transition-all"
                          >
                            <span className="font-medium text-sm">{template.name}</span>
                            <p className="text-xs text-muted-foreground mt-1">
                              {template.ingredients.length} ingredients, {template.modifiers.length} options
                            </p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No templates available for this category.</p>
                    )}
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={parseIngredientsFromDescription}
                      className="w-full"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Auto-Parse from Description
                    </Button>
                  </div>
                )}
              </div>

              {/* Info Box - Simplified Explanation */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">How Ingredients Work</h4>
                <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                  <p><strong>🔒 Essential:</strong> Customer cannot remove (e.g., bread on a sandwich)</p>
                  <p><strong>🔓 Optional:</strong> Customer can request to remove at no charge</p>
                  <p><strong>💰 Extra Price:</strong> Set if adding MORE of this ingredient costs extra</p>
                </div>
              </div>

              {/* Existing Ingredients */}
              {formData.ingredients.length > 0 && (
                <div className="space-y-2">
                  {formData.ingredients.map((ing, index) => (
                    <div key={`ing-${ing.name}-${index}`} className="flex items-center gap-2 p-3 bg-muted rounded-xl">
                      <span className="flex-1 font-medium text-sm">{ing.name}</span>
                      
                      {/* iOS-style Essential Toggle */}
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium transition-colors ${ing.removable === false ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`}>
                          {ing.removable === false ? (
                            <span className="flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Essential
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Unlock className="w-3 h-3" />
                              Optional
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateIngredient(index, 'removable', ing.removable === false ? true : false)}
                          data-testid={`toggle-essential-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            ing.removable === false 
                              ? 'bg-blue-600' 
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          title={ing.removable === false ? 'Click to make optional (customers can remove)' : 'Click to make essential (customers cannot remove)'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                              ing.removable === false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs text-muted-foreground">Extra:</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={ing.extra_price || ''}
                          onChange={(e) => updateIngredient(index, 'extra_price', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="w-16 h-7 text-xs"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeIngredient(index)}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 dark:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Ingredient */}
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Ingredient Name</Label>
                  <Input
                    value={newIngredient.name}
                    onChange={(e) => setNewIngredient(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Pickles, Onions, Lettuce"
                  />
                </div>
                <div className="w-24">
                  <Label className="text-xs">Extra Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newIngredient.extra_price}
                    onChange={(e) => setNewIngredient(prev => ({ ...prev, extra_price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                {/* iOS-style toggle for new ingredient */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium ${!newIngredient.removable ? 'text-blue-600' : 'text-muted-foreground'}`}>
                    {!newIngredient.removable ? 'Essential' : 'Optional'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setNewIngredient(prev => ({ ...prev, removable: !prev.removable }))}
                    data-testid="toggle-new-ingredient-essential"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      !newIngredient.removable 
                        ? 'bg-blue-600' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    title={!newIngredient.removable ? 'Essential (customers cannot remove)' : 'Optional (customers can remove)'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                        !newIngredient.removable ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <Button type="button" size="sm" onClick={addIngredient}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Modifiers Tab */}
          <TabsContent value="modifiers">
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                <p className="text-sm text-muted-foreground">
                  Modifiers are special options like "No Ice", "Gluten-Free Bun", or milk alternatives. 
                  These can have fixed prices or size-specific pricing.
                </p>
              </div>

              {/* No Ice Pricing Info */}
              {hasNoIceModifier && formData.sizes.length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">
                    Size-Specific "No Ice" Pricing
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                    Set different prices for "No Ice" based on drink size. Standard: $1.00 small, $1.50 large.
                  </p>
                  <div className="space-y-2">
                    {formData.sizes.map(size => (
                      <div key={size.name} className="flex items-center gap-3">
                        <span className="w-24 text-sm font-medium">{size.name}:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={getSizeModifierPrice(size.name, 'No Ice')}
                            onChange={(e) => updateSizeModifierPrice(size.name, 'No Ice', e.target.value)}
                            placeholder={size.name.toLowerCase().includes('large') ? '1.50' : '1.00'}
                            className="w-20 h-8 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Modifiers */}
              {formData.modifiers.length > 0 && (
                <div className="space-y-2">
                  {formData.modifiers.map((mod, index) => (
                    <div key={`mod-${mod.name}-${index}`} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                      <div className="flex-1">
                        <span className="font-medium text-sm">{mod.name}</span>
                        {mod.description && (
                          <p className="text-xs text-muted-foreground">{mod.description}</p>
                        )}
                      </div>
                      {mod.price > 0 && (
                        <span className="text-primary text-sm font-medium">+${mod.price.toFixed(2)}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeModifier(index)}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 dark:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Modifier */}
              <div className="p-4 border border-dashed border-border rounded-xl space-y-3">
                <p className="text-sm font-medium">Add New Modifier</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Modifier Name *</Label>
                    <Input
                      value={newModifier.name}
                      onChange={(e) => setNewModifier(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., No Ice, Oat Milk"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Price Adjustment (FJD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newModifier.price}
                      onChange={(e) => setNewModifier(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Input
                    value={newModifier.description}
                    onChange={(e) => setNewModifier(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Extra liquid added to compensate"
                  />
                </div>
                <Button type="button" onClick={addModifier} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Modifier
                </Button>
              </div>
              
              {/* Quick Add Common Modifiers */}
              <div className="p-3 bg-muted/50 rounded-xl">
                <p className="text-xs font-medium mb-2">Quick Add Common Modifiers:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: 'No Ice', price: 1.00, description: 'Extra liquid added to compensate' },
                    { name: 'Light Ice', price: 0, description: 'Less ice than normal' },
                    { name: 'Oat Milk', price: 0.75, description: 'Substitute with oat milk' },
                    { name: 'Almond Milk', price: 0.75, description: 'Substitute with almond milk' },
                    { name: 'Decaf', price: 0, description: 'Decaffeinated' },
                    { name: 'Gluten-Free', price: 2.00, description: 'Gluten-free option' },
                  ].filter(m => !formData.modifiers.some(existing => existing.name.toLowerCase() === m.name.toLowerCase()))
                   .map(mod => (
                    <button
                      key={mod.name}
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        modifiers: [...prev.modifiers, mod]
                      }))}
                      className="px-3 py-1 text-xs bg-card border border-border rounded-full hover:border-primary transition-colors"
                    >
                      + {mod.name} {mod.price > 0 && `($${mod.price.toFixed(2)})`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-3 pt-4 border-t border-border mt-4">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="flex-1 rounded-full bg-primary hover:bg-primary/90" data-testid="save-menu-item-btn">
            {loading ? 'Saving...' : (item ? 'Update Item' : 'Add Item')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
